/**
 * GA4 `chunk_recovery` — counts how often a deploy pulls a user out of a view
 * (docs/spec/01c-sw-update-flow.md, "Evidence-based stale-chunk recovery",
 * paragraph "Telemetry"). The count decides whether keeping old builds' /static
 * files online (plan step V9) is worth building.
 *
 * The OLD page cannot send the event: an event sent right before a reload can
 * be lost, and analytics logs success only. So the boot script's recovery
 * leaves `{ path, at }` in sessionStorage (`noteChunkRecovery` in index.html)
 * right before each reload it causes, and the NEW page — the one that booted
 * again — reports it once, a few seconds later. Same shape as
 * `reportForcedUpdate` in hooks/useClientCompat.js.
 */
import { useEffect, useRef } from "react";
import { buildId as BUILD_ID } from "../buildInfo";
import { logEvent, EVT } from "../analytics";
import { REPORT_DELAY_MS } from "../hooks/useClientCompat";
import { PUBLIC_DASHBOARD_RE } from "./publicRoutes";
import { sessionStore } from "./safeStorage";

/** The record the boot script writes; kept in step with `noteChunkRecovery` in index.html. */
export const CHUNK_RECOVERY_KEY = "sendly-chunk-recovery";

/** A record older than this belongs to a reload that never happened (or an unrelated boot). */
export const CHUNK_RECOVERY_MAX_AGE_MS = 5 * 60 * 1000;

/** The values the boot script writes — anything else is dropped, never sent. */
export const CHUNK_RECOVERY_PATHS = Object.freeze([
  "light_reload", "waiting_sw", "hard", "stalled_reload", "offline_reload",
]);

let takenRecord; // undefined = not read yet in this page
let reported = false;

/**
 * Read the record once per page and delete it at once, so a later reload in the
 * same tab can never report it twice. Taken at mount, before anything else.
 * Returns `{ path, at }` or null (no record, unreadable, malformed).
 */
export function takeChunkRecoveryRecord() {
  if (takenRecord !== undefined) return takenRecord;
  takenRecord = null;
  const raw = sessionStore.get(CHUNK_RECOVERY_KEY);
  if (raw !== null) {
    sessionStore.remove(CHUNK_RECOVERY_KEY);
    try {
      const rec = JSON.parse(raw);
      if (rec && typeof rec === "object" && typeof rec.path === "string" && typeof rec.at === "number") {
        takenRecord = { path: rec.path, at: rec.at };
      }
    } catch {
      // The record is not JSON: nothing to report.
    }
  }
  return takenRecord;
}

/**
 * The event's params for a record, or null when nothing is to be sent: no
 * record, an unknown path, a record older than five minutes, a public
 * dashboard route or a non-'app' path kind (no consent flow runs there).
 */
export function chunkRecoveryEventParams(record, { now = Date.now(), pathname, pathKind, gymId } = {}) {
  if (!record || !CHUNK_RECOVERY_PATHS.includes(record.path)) return null;
  if (!Number.isFinite(record.at) || now - record.at > CHUNK_RECOVERY_MAX_AGE_MS) return null;
  if (typeof pathname === "string" && PUBLIC_DASHBOARD_RE.test(pathname)) return null;
  if (pathKind !== "app") return null;
  const params = { recovery_path: record.path };
  if (gymId) params.gym_id = gymId;
  return params;
}

function readPathKind() {
  try {
    return typeof window.__sendlyPathKind === "function" ? window.__sendlyPathKind() : null;
  } catch {
    return null;
  }
}

/** Send the event at most once per page. Returns whether it was sent. */
export function reportChunkRecovery({ gymId = null, log = logEvent, now = Date.now() } = {}) {
  if (reported) return false;
  const params = chunkRecoveryEventParams(takeChunkRecoveryRecord(), {
    now,
    pathname: typeof location !== "undefined" ? location.pathname : "",
    pathKind: readPathKind(),
    gymId,
  });
  if (!params) return false;
  reported = true;
  log(EVT.CHUNK_RECOVERY, params);
  return true;
}

/** Test seam: forget the per-page record / report state. */
export function __resetChunkRecoveryReportForTests() {
  takenRecord = undefined;
  reported = false;
}

/**
 * Mounted once, at the root of `App`. Takes (and deletes) the record at mount,
 * then reports ~5 s later — a boot that held, by when a gym route has usually
 * resolved the `gym_id`.
 *
 * @param {{ gymId?: string|null }} [opts] the active gym, for the GA4 `gym_id`
 */
export function useChunkRecoveryReport({ gymId = null } = {}) {
  const gymIdRef = useRef(gymId);
  gymIdRef.current = gymId;

  useEffect(() => {
    // Delete the record whether or not it is sent (dev, public routes, ...).
    takeChunkRecoveryRecord();
    // No build id = the dev server: it has no recovery and no consent flow.
    if (!BUILD_ID) return undefined;
    const timer = setTimeout(() => {
      reportChunkRecovery({ gymId: gymIdRef.current });
    }, REPORT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);
}
